import React, { Component } from 'react'
import { connect } from 'react-redux'

import { updateTask } from 'state/actions'
import { getCurrentUser } from 'state/selectors'

import copy from 'fast-copy'
import linkifyHtml from 'linkifyjs/html'

import Button from 'components/button/button'
import Icon from 'components/icon/icon'
import ProfilePicture from 'components/profile/profile-picture'
import QuillWysiwyg from 'components/input/quill-wysiwyg'

import { formatByTimeZone, getDateString, getMentionableUsers, safelyFormatText, stringIsEmpty } from 'utils'

// Same treatment comments get before they're rendered back out. See
// formatText in message-list/message-group.jsx.
const formatDescription = (text) => {
    if (!text) {
        return ''
    }

    let descriptionText = text.replace('<p></p>', '')
    descriptionText = descriptionText.replace('<p><br></p>', '')

    if (!descriptionText) {
        return ''
    }

    try {
        descriptionText = linkifyHtml(descriptionText, {})
    } catch (e) {
        console.log(e)
    }

    descriptionText = safelyFormatText(descriptionText)

    descriptionText = descriptionText.replace(/ href/g, ' target="_blank" href')
    descriptionText = descriptionText.replace(/_self/g, '_blank')

    return descriptionText
}

class SingleTaskDescription extends Component {
    // taskObject.description is the saved value; draftText is the unsaved edit
    // sitting in the editor. There's no third copy.
    state = {
        draftText: this.props.taskObject.description || '',
        isEditing: false,
    }

    UNSAFE_componentWillReceiveProps = (nextProps) => {
        const { taskObject } = this.props

        const isDifferentTask = nextProps.taskObject.id !== taskObject.id

        // Only pull in a remote change when there's nothing unsaved to clobber.
        const hasRemoteChange =
            nextProps.taskObject.description !== taskObject.description && this.isUnchanged()

        if (isDifferentTask || hasRemoteChange) {
            this.setState({
                draftText: nextProps.taskObject.description || '',
                isEditing: isDifferentTask ? false : this.state.isEditing,
            })
        }
    }

    handleChange = (value) => {
        const valueWithoutStyleTags = value.replace(/style="[a-zA-Z0-9:;\.\s\(\)\-\,]*"/gi, '')

        this.setState({
            draftText: valueWithoutStyleTags,
        })
    }

    startEditing = (_) => {
        this.setState({
            isEditing: true,
        })
    }

    cancelEditing = (_) => {
        this.setState({
            draftText: this.props.taskObject.description || '',
            isEditing: false,
        })
    }

    saveDescription = (_) => {
        const { taskObject, userData, updateTask } = this.props

        if (!userData) {
            return
        }

        const updatedTask = copy(taskObject)

        updateTask({
            ...updatedTask,
            describer: userData,
            description_updated_at: getDateString(),
            description: this.state.draftText,
        })

        this.setState({
            isEditing: false,
        })
    }

    isUnchanged = (_) => {
        const { draftText } = this.state

        const savedText = this.props.taskObject.description || ''

        if (stringIsEmpty(draftText) && stringIsEmpty(savedText)) {
            return true
        }

        return draftText === savedText
    }

    getDescriber = (_) => {
        const { taskObject, allUsers } = this.props

        const { describer } = taskObject

        if (!describer) {
            return null
        }

        // Saved as the whole user, but the API may hand it back as just an id.
        if (typeof describer === 'object') {
            return describer
        }

        return allUsers ? allUsers[describer] : null
    }

    renderReadView = (_) => {
        const { taskObject, isArchived, isProjectEditable } = this.props

        const canEdit = !isArchived && isProjectEditable

        const formattedText = formatDescription(taskObject.description)

        const describer = this.getDescriber()

        return (
            <React.Fragment>
                {canEdit && (
                    <div className="description-controls tooltip-trigger">
                        <Icon onClick={this.startEditing} name="fe-edit-2" />
                        <div className="tooltip">Edit description</div>
                    </div>
                )}
                <div className="description-content" dangerouslySetInnerHTML={{ __html: formattedText }} />
                {describer && (
                    <div className="description-meta">
                        <ProfilePicture user={describer} size="tiny" />
                        <div className="description-meta-text">
                            <strong>{`${describer.first_name} ${describer.last_name}`}</strong>
                            {taskObject.description_updated_at && (
                                <span>{formatByTimeZone(taskObject.description_updated_at, 'Do MMMM, h:mm a')}</span>
                            )}
                        </div>
                    </div>
                )}
            </React.Fragment>
        )
    }

    renderEditView = (_) => {
        const { taskObject, applicableUsers } = this.props

        const { draftText } = this.state

        // With nothing saved to fall back to there's nothing to cancel to.
        const canCancel = !!formatDescription(taskObject.description)

        return (
            <React.Fragment>
                <QuillWysiwyg
                    isVisible={true}
                    value={draftText}
                    onChange={this.handleChange}
                    ref={(ref) => (this.quillRef = ref)}
                    canCollaborate={false}
                    objectId={taskObject.id}
                    availableMentions={getMentionableUsers(applicableUsers)}
                    canEdit={true}
                    placeholder="Write a description for this task."
                    context="description"
                />
                <div className="description-footer clear">
                    <Button
                        size={'big'}
                        level={'high'}
                        callback={this.saveDescription}
                        label={'Save description'}
                        icon={'fe-check'}
                        disabled={this.isUnchanged()}
                    />
                    {canCancel && <Button size={'big'} level={'low'} callback={this.cancelEditing} label={'Cancel'} />}
                </div>
            </React.Fragment>
        )
    }

    render() {
        const { taskObject, isArchived, isProjectEditable } = this.props

        const { isEditing } = this.state

        const canEdit = !isArchived && isProjectEditable

        // Nothing written yet? Skip the read view and drop straight into the editor.
        const hasDescription = !!formatDescription(taskObject.description)

        const showEditor = canEdit && (isEditing || !hasDescription)

        return (
            <div className="single-task-description">{showEditor ? this.renderEditView() : this.renderReadView()}</div>
        )
    }
}

const mapStateToProps = (state) => ({
    userData: getCurrentUser(state),
    allUsers: state.account.entities.users,
})

export default connect(mapStateToProps, { updateTask })(SingleTaskDescription)
